"use client";

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ApplicationDetail,
  ApplicationListItem,
  ApplicationStats,
  DetailModalTab,
  NotesModalType,
} from "@/components/BusinessApplicationsParent/types";

// Pagination interface
interface ApplicationsPagination {
  count: number;
  page: number;
  pageSize: number;
}

// Detail modal state
interface DetailModalState {
  open: boolean;
  applicationId: string | null;
  activeTab: DetailModalTab;
}

// Notes modal state
interface NotesModalState {
  open: boolean;
  type: NotesModalType;
}

// Provisioning account credentials
export interface ProvisioningAccount {
  full_name: string;
  role: string;
  is_primary: boolean;
  crm_login: { email: string; password: string };
  pos_login: { staff_id: string; pin: string };
}

// Domain setup info shown after registration. SSL is provisioned by Caddy
// on first request — the panel polls /domain-status for live state.
export interface DomainSetupResult {
  domain: string;
  tenantType?: "tenant" | "supplier";
  vps_ip?: string;
}

// Credentials modal state
interface CredentialsModalState {
  open: boolean;
  accounts: ProvisioningAccount[];
  businessName: string;
  domainSetup: DomainSetupResult | null;
}

// Slice state interface
interface BusinessApplicationsState {
  // Applications list
  applications: ApplicationListItem[];
  applicationsLoading: boolean;
  applicationsPagination: ApplicationsPagination;

  // Stats
  stats: ApplicationStats | null;
  statsLoading: boolean;

  // Detail modal
  detailModal: DetailModalState;
  selectedApplication: ApplicationDetail | null;
  selectedApplicationLoading: boolean;

  // Notes modal
  notesModal: NotesModalState;

  // Credentials modal (shown after successful approval)
  credentialsModal: CredentialsModalState;

  // Action loading (approve/reject/requirements)
  actionLoading: boolean;
}

// Initial state
const initialState: BusinessApplicationsState = {
  applications: [],
  applicationsLoading: false,
  applicationsPagination: {
    count: 0,
    page: 1,
    pageSize: 25,
  },

  stats: null,
  statsLoading: false,

  detailModal: {
    open: false,
    applicationId: null,
    activeTab: "business",
  },
  selectedApplication: null,
  selectedApplicationLoading: false,

  notesModal: {
    open: false,
    type: null,
  },

  credentialsModal: {
    open: false,
    accounts: [],
    businessName: "",
    domainSetup: null,
  },

  actionLoading: false,
};

const businessApplicationsSlice = createSlice({
  name: "businessApplications",
  initialState,
  reducers: {
    // Applications list
    setApplications: (state, action: PayloadAction<ApplicationListItem[]>) => {
      state.applications = action.payload;
    },
    setApplicationsLoading: (state, action: PayloadAction<boolean>) => {
      state.applicationsLoading = action.payload;
    },
    setApplicationsPagination: (
      state,
      action: PayloadAction<ApplicationsPagination>,
    ) => {
      state.applicationsPagination = action.payload;
    },
    updateApplicationsPaginationPage: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.applicationsPagination.page = action.payload;
    },
    updateApplicationsPaginationPageSize: (
      state,
      action: PayloadAction<number>,
    ) => {
      state.applicationsPagination.pageSize = action.payload;
      state.applicationsPagination.page = 1; // Reset to first page when page size changes
    },

    // Stats
    setStats: (state, action: PayloadAction<ApplicationStats>) => {
      state.stats = action.payload;
    },
    setStatsLoading: (state, action: PayloadAction<boolean>) => {
      state.statsLoading = action.payload;
    },

    // Detail modal
    openDetailModal: (state, action: PayloadAction<string>) => {
      state.detailModal.open = true;
      state.detailModal.applicationId = action.payload;
      state.detailModal.activeTab = "business";
    },
    closeDetailModal: (state) => {
      state.detailModal.open = false;
      state.detailModal.applicationId = null;
      state.detailModal.activeTab = "business";
      state.selectedApplication = null;
    },
    setDetailModalTab: (state, action: PayloadAction<DetailModalTab>) => {
      state.detailModal.activeTab = action.payload;
    },
    setSelectedApplication: (
      state,
      action: PayloadAction<ApplicationDetail | null>,
    ) => {
      state.selectedApplication = action.payload;
    },
    setSelectedApplicationLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedApplicationLoading = action.payload;
    },

    // Notes modal
    openNotesModal: (state, action: PayloadAction<NotesModalType>) => {
      state.notesModal.open = true;
      state.notesModal.type = action.payload;
    },
    closeNotesModal: (state) => {
      state.notesModal.open = false;
      state.notesModal.type = null;
    },

    // Credentials modal
    openCredentialsModal: (
      state,
      action: PayloadAction<{
        accounts: ProvisioningAccount[];
        businessName: string;
        domainSetup?: DomainSetupResult | null;
      }>,
    ) => {
      state.credentialsModal.open = true;
      state.credentialsModal.accounts = action.payload.accounts;
      state.credentialsModal.businessName = action.payload.businessName;
      state.credentialsModal.domainSetup = action.payload.domainSetup ?? null;
    },
    closeCredentialsModal: (state) => {
      state.credentialsModal.open = false;
      state.credentialsModal.accounts = [];
      state.credentialsModal.businessName = "";
      state.credentialsModal.domainSetup = null;
    },

    // Action loading
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },

    // Reset state
    resetBusinessApplicationsState: () => initialState,
  },
});

export const {
  setApplications,
  setApplicationsLoading,
  setApplicationsPagination,
  updateApplicationsPaginationPage,
  updateApplicationsPaginationPageSize,
  setStats,
  setStatsLoading,
  openDetailModal,
  closeDetailModal,
  setDetailModalTab,
  setSelectedApplication,
  setSelectedApplicationLoading,
  openNotesModal,
  closeNotesModal,
  openCredentialsModal,
  closeCredentialsModal,
  setActionLoading,
  resetBusinessApplicationsState,
} = businessApplicationsSlice.actions;

export default businessApplicationsSlice.reducer;
